import { registerPlugin, type PluginListenerHandle } from "@capacitor/core";

/**
 * JS side of the app-local `InlineBanner` Capacitor plugin
 * (apps/mobile-{cr,cn}/android/app/src/main/java/.../InlineBannerPlugin.java).
 * Ported from Itinero.
 *
 * All positions are CSS px. `y` is the placeholder's DOCUMENT offset
 * (rect.top + scrollY) — it doesn't change while scrolling, so the native side
 * tracks the WebView's scroll on its own every frame. That's why the phone
 * layout scrolls the document rather than an inner overflow container.
 * `clipTop` / `clipBottom` are the insets from the top / bottom of the WebView
 * the banner is clipped to (sticky header, bottom tab bar).
 */
export interface InlineBannerGeometry {
  x: number;
  y: number;
  clipTop: number;
  clipBottom: number;
}

export interface InlineBannerPlugin {
  create(
    options: InlineBannerGeometry & {
      id: string;
      adId: string;
      width: number;
      maxHeight: number;
    },
  ): Promise<void>;
  update(options: Partial<InlineBannerGeometry> & { id: string }): Promise<void>;
  setVisible(options: { id: string; visible: boolean }): Promise<void>;
  remove(options: { id: string }): Promise<void>;
  addListener(
    event: "bannerLoaded",
    fn: (e: { id: string; width: number; height: number }) => void,
  ): Promise<PluginListenerHandle>;
  addListener(
    event: "bannerFailed",
    fn: (e: { id: string; code: number; message: string }) => void,
  ): Promise<PluginListenerHandle>;
}

export const InlineBanner = registerPlugin<InlineBannerPlugin>("InlineBanner");
